/**
 * learnerLevel.js — ONE child-facing level: "Age 8 · Year 4". Never silently assumes Year 4.
 * Source order: active child profile → explicitly saved level (mwq_learner_level_v1) → null (ASK).
 * Parents may override the year; internally we store { age, year, ageBand, curriculum }.
 */
import { useEffect, useState } from "react";
import { getActiveChild } from "@/lib/children";
import { updateProfile } from "@/lib/profileStore";
import { bandForAge, yearForAge } from "@/lib/canonical";

const KEY = "mwq_learner_level_v1";
const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } };

export function getLevel() {
  const child = getActiveChild();
  const saved = read();
  if (child && Number(child.age)) {
    const age = Number(child.age);
    const year = saved?.childId === child.id && saved.yearOverride ? saved.yearOverride : yearForAge(age);
    return { age, year, ageBand: bandForAge(age), curriculum: child.curriculum || saved?.curriculum || "", source: "child", childId: child.id, name: child.name || "" };
  }
  if (saved && Number(saved.age)) return { age: Number(saved.age), year: saved.yearOverride || yearForAge(saved.age), ageBand: bandForAge(saved.age), curriculum: saved.curriculum || "", source: "saved", childId: null, name: "" };
  return null;
}

export function setLevel({ age, year, curriculum }) {
  const child = getActiveChild();
  const cur = read() || {};
  const next = { ...cur, childId: child?.id || null };
  if (age != null) { next.age = Math.max(4, Math.min(15, Number(age))); next.yearOverride = null; if (child) updateProfile({ age: next.age }); }
  if (year != null) next.yearOverride = Math.max(1, Math.min(10, Number(year)));
  if (curriculum != null) { next.curriculum = curriculum; if (child) updateProfile({ curriculum }); }
  try { localStorage.setItem(KEY, JSON.stringify(next)); window.dispatchEvent(new CustomEvent("mwq-level-changed")); } catch { /* ignore */ }
  return getLevel();
}

export const levelLabel = (l) => (l ? `Age ${l.age} · Year ${l.year}` : "");

export function useLevel() {
  const [lvl, set] = useState(getLevel);
  useEffect(() => { const h = () => set(getLevel()); ["mwq-level-changed", "we-profile-changed", "we-children-changed", "storage"].forEach((e) => window.addEventListener(e, h)); return () => ["mwq-level-changed", "we-profile-changed", "we-children-changed", "storage"].forEach((e) => window.removeEventListener(e, h)); }, []);
  return lvl;
}
